import { useCallback, useMemo } from 'react';
import cn from 'classnames';
import useCheckboxRangeSelection, {
  type TGetCheckboxProps,
} from '@creditflowresearch/uilib/hooks/useCheckboxRangeSelection';
import type { IColumn } from '@creditflowresearch/uilib/Table/DataTable/interfaces/Column';
import type { ICellProps } from '@creditflowresearch/uilib/Table/DataTable';
import type { IColumnDefinition } from '@/creditflowresearch/ui/common/components/DataTable/columns';
import { resolveAlignment, resolveColumnFromApiConfig } from '@/creditflowresearch/ui/common/components/DataTable/columns';
import type {
  IApiColumnConfig,
  ICorporateBondScreenerTrancheResponse,
} from '@/creditflowresearch/domain/vo/Bondscreener';
import {
  useBondScreenerColumnsContext,
} from '@/creditflowresearch/application/BondScreener/BondScreenerColumnsContext';
import {
  useCorporateBondScreenerMySelectionContext,
} from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerMySelectionContext';
import {
  aboveRowLinkClassName,
  createRowLinkColumnDefinition,
} from '@/creditflowresearch/ui/common/components/DataTable/rowLink';
import columnRendererRegistry, { LOCKED_COLUMN_IDS } from '../columns/columnRendererRegistry';
import COLUMN_DATA_FIELD_MAP from '../columns/columnDataFieldMap';
import { CorporateBondScreenerCheckboxCell } from '../CorporateBondScreenerCheckboxCell';

import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

const createCheckboxColumn = (getCheckboxProps: TGetCheckboxProps): IColumnDefinition => ({
  name: 'checkbox',
  title: '',
  dataType: 'checkbox',
  width: 40,
  minWidth: 40,
  maxWidth: 40,
  sortable: false,
  isDraggable: false,
  className: aboveRowLinkClassName,
  cellCallback: ({ row, rowIndex }: ICellProps) => (
    <CorporateBondScreenerCheckboxCell row={ row } rowIndex={ rowIndex } getCheckboxProps={ getCheckboxProps } />
  ),
});

const SPACER_CELL: IColumnDefinition = {
  name: 'spacer',
  title: '',
  dataType: 'checkbox',
  width: 'auto',
  minWidth: 0,
  maxWidth: 0,
  sortable: false,
  isDraggable: false,
  className: spacesStyles.pn,
  cellCallback: () => <div />,
};

const buildColumnsRegistry = (apiColumns: readonly IApiColumnConfig[]): ReadonlyMap<string, IColumnDefinition> => {
  const registry = new Map<string, IColumnDefinition>();

  for (const apiCol of apiColumns) {
    const localEntry = columnRendererRegistry[apiCol.id];
    const dataField = COLUMN_DATA_FIELD_MAP[apiCol.id] ?? apiCol.id;

    registry.set(apiCol.id, resolveColumnFromApiConfig(apiCol, localEntry, dataField));
  }

  return registry;
};

export const useDynamicColumns = (rows: readonly ICorporateBondScreenerTrancheResponse[]): IColumn[] => {
  const { activeColumnsIds, allColumns } = useBondScreenerColumnsContext();
  const { selectedIds, setItemsSelected } = useCorporateBondScreenerMySelectionContext();

  const isRowSelected = useCallback(
    (row: ICorporateBondScreenerTrancheResponse): boolean => selectedIds.has(row.id),
    [selectedIds],
  );

  const getCheckboxProps = useCheckboxRangeSelection<ICorporateBondScreenerTrancheResponse>({
    items: rows,
    isSelected: isRowSelected,
    onChange: setItemsSelected,
  });

  const columnsRegistry = useMemo(
    () => buildColumnsRegistry(allColumns),
    [allColumns],
  );

  return useMemo(() => {
    const ordered: IColumnDefinition[] = [createCheckboxColumn(getCheckboxProps)];

    for (const id of LOCKED_COLUMN_IDS) {
      const col = columnsRegistry.get(id);
      if (col) {
        ordered.push({ ...col, stickyLeft: true });
      }
    }

    for (const id of activeColumnsIds) {
      if (LOCKED_COLUMN_IDS.has(id)) continue;
      const col = columnsRegistry.get(id);
      if (col) {
        ordered.push(col);
      }
    }

    ordered.push(
      createRowLinkColumnDefinition<ICorporateBondScreenerTrancheResponse>(
        (row) => row.url,
        { target: '_blank' },
      ),
    );
    ordered.push(SPACER_CELL);

    const firstDataIndex = ordered.findIndex((col) => col.dataType !== 'checkbox');
    const lastDataIndex = ordered.findLastIndex((col) => col.dataType !== 'checkbox');

    return ordered.map((col, index) => ({
      name: col.name,
      title: col.title,
      width: col.width,
      minWidth: col.minWidth,
      maxWidth: col.maxWidth,
      sortable: col.sortable,
      tooltipContent: col.tooltipContent,
      tooltipMaxWidth: col.tooltipMaxWidth,
      isDraggable: col.isDraggable,
      stickyLeft: col.stickyLeft,
      className: cn(
        resolveAlignment(col.dataType, {
          isFirstDataColumn: index === firstDataIndex,
          isLastColumn: index === lastDataIndex,
          customAlign: col.customAlign,
        }),
        col.className,
      ),
      cellCallback: col.cellCallback,
    }));
  }, [columnsRegistry, activeColumnsIds, getCheckboxProps]);
};
